// Who's behind a number (customers.findByPhone), the first thing seen when the phone rings or an AI tool
// is asked "who's calling?". One match also reads that customer at a glance (customers.glance, through
// the host, as the person); none offers a new customer; several are listed.
import { useEffect, useState } from 'react';
import type { CallerMatchesProps, CustomerGlanceProps } from '../schemas.ts';
import { CustomerGlanceProps as GlanceSchema } from '../schemas.ts';
import type { ViewProps } from '../host.ts';
import { phone } from '../format.ts';
import { Action, Label } from '../primitives.tsx';
import { CustomerGlance } from './CustomerGlance.tsx';

export function CallerMatches({ data, host }: ViewProps<CallerMatchesProps>) {
  const chat = host.surface === 'chat';
  const one = data.matches.length === 1 ? data.matches[0]! : null;
  const [glance, setGlance] = useState<CustomerGlanceProps | 'none' | null>(null);
  useEffect(() => {
    if (!one) return;
    let live = true;
    void host.callTool('customers.glance', { customerId: one.customerId }).then((a) => {
      if (!live) return;
      const parsed = a.ok ? GlanceSchema.safeParse(a.result) : null;
      setGlance(parsed?.success ? parsed.data : 'none');
    });
    return () => {
      live = false;
    };
  }, [one?.customerId, host]);

  const number = phone(data.phone);
  if (!data.phone)
    return (
      <p className="flw-empty" role="status">
        That isn’t a phone number Flowology can look up.
      </p>
    );

  if (data.matches.length === 0)
    return (
      <article aria-label={`Nobody at ${number}`}>
        <h3 className="flw-title">No one here has {number}</h3>
        <p className="flw-sub">A new caller. Their name and address are enough to start.</p>
        <div className="flw-acts">
          <Action primary onClick={() => host.open({ kind: 'book', phone: data.phone! })}>
            Book a new customer
          </Action>
          {chat && (
            <Action onClick={() => host.open({ kind: 'book', phone: data.phone! })}>Open in Flowology</Action>
          )}
        </div>
      </article>
    );

  if (one)
    return (
      <article aria-label={`${number}: ${one.contactName}`}>
        <p className="flw-sub">
          {number}: {one.contactName}
          {one.contactName !== one.customerName && <>, {one.customerName}</>}
        </p>
        {glance && glance !== 'none' ? (
          <CustomerGlance data={glance} host={host} />
        ) : (
          <>
            <h3 className="flw-title">{one.customerName}</h3>
            {glance === null ? (
              <p className="flw-sub" aria-busy="true">
                Reading their record…
              </p>
            ) : null}
            <div className="flw-acts">
              <Action primary onClick={() => host.open({ kind: 'book', customerId: one.customerId })}>
                Book a visit
              </Action>
              <Action onClick={() => host.open({ kind: 'customer', id: one.customerId })}>
                {chat ? 'Open in Flowology' : 'Their record'}
              </Action>
            </div>
          </>
        )}
      </article>
    );

  const listed = data.matches.slice(0, 5);
  return (
    <article aria-label={`${data.matches.length} customers at ${number}`}>
      <h3 className="flw-title">
        {data.matches.length} customers share {number}
      </h3>
      <p className="flw-sub">
        <Label kind="wait">Ask who’s calling</Label> before you book.
      </p>
      <ul className="flw-rows">
        {listed.map((m) => (
          <li key={`${m.customerId}-${m.contactId}`} className="flw-row">
            <span>
              <b>{m.customerName}</b>
              <span className="flw-sub">{m.contactName}</span>
            </span>
            <Action
              onClick={() => host.open({ kind: 'customer', id: m.customerId })}
              label={`Open ${m.customerName}`}
            >
              Open
            </Action>
          </li>
        ))}
      </ul>
      {data.matches.length > listed.length && (
        <p className="flw-sub">And {data.matches.length - listed.length} more.</p>
      )}
    </article>
  );
}
